import { useState } from 'react';
import { X, Save, Plus, Pencil } from 'lucide-react';
import apiClient from '../api/client';
import JsonEditor from './JsonEditor';

export interface SkillItem {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  definition: Record<string, unknown>;
  source: string;
  priority: number;
  is_enabled: boolean;
}

interface SkillEditorProps {
  skill?: SkillItem | null;
  profileId: string;
  onSave: () => void;
  onCancel: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-lg)',
  width: '90%',
  maxWidth: 640,
  maxHeight: '90vh',
  overflow: 'auto',
  padding: 24,
};

// 从 axios 错误中提取后端 detail 文案
function apiErrorDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  return typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
}

export default function SkillEditor({ skill, profileId, onSave, onCancel }: SkillEditorProps) {
  const isEdit = !!skill;
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [definition, setDefinition] = useState<object>({});
  const [priority, setPriority] = useState(0);
  const [isEnabled, setIsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 渲染期调整：skill prop 变化时同步表单（替代原 useEffect）
  const [prevSkill, setPrevSkill] = useState(skill);
  if (prevSkill !== skill) {
    setPrevSkill(skill);
    if (skill) {
      setName(skill.name);
      setDisplayName(skill.display_name);
      setDescription(skill.description || '');
      setDefinition(skill.definition || {});
      setPriority(skill.priority);
      setIsEnabled(skill.is_enabled);
    } else {
      setName('');
      setDisplayName('');
      setDescription('');
      setDefinition({});
      setPriority(0);
      setIsEnabled(true);
    }
    setFormError(null);
  }

  const handleSubmit = async () => {
    if (!name.trim() || !displayName.trim()) {
      setFormError('标识名和展示名为必填');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        display_name: displayName.trim(),
        description: description.trim() || null,
        definition,
        priority,
        is_enabled: isEnabled,
      };
      if (isEdit && skill) {
        await apiClient.put(`/profiles/${profileId}/skills/${skill.id}`, payload);
      } else {
        await apiClient.post(`/profiles/${profileId}/skills`, payload);
      }
      onSave();
    } catch (e) {
      setFormError(apiErrorDetail(e) || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onCancel}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ margin: 0 }}>
            {isEdit ? <><Pencil size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />编辑技能</> : <><Plus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />新增技能</>}
          </h3>
          <button className="secondary" style={{ padding: '6px 8px' }} onClick={onCancel}>
            <X size={16} />
          </button>
        </div>

        {formError && (
          <div className="card mb-4" style={{ borderColor: 'var(--color-error)', background: 'rgba(248,81,73,0.1)' }}>
            <p style={{ color: 'var(--color-error)', margin: 0, fontSize: 13 }}>{formError}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              标识名 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：diet_planning"
              disabled={isEdit}
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
              展示名 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="如：饮食方案制定"
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="技能功能描述"
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Definition (JSON)</label>
            <JsonEditor value={definition} onChange={setDefinition} placeholder='{"input_schema": {}, "output_schema": {}}' />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>优先级 (0-100)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={priority}
                onChange={(e) => setPriority(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              />
            </div>
            <div className="flex items-center" style={{ paddingTop: 24 }}>
              <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                <span className="text-sm text-muted">启用</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button className="primary" onClick={handleSubmit} disabled={saving} style={{ flex: 1 }}>
            <Save size={14} /> {saving ? '保存中...' : '保存'}
          </button>
          <button className="secondary" onClick={onCancel} disabled={saving}>
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
